/**
 * A data subject's export of their own account: `GET /api/account/export`
 * (audit E10). JSON, versioned, owner only. It carries the subject's own
 * data by class and never a secret (token, hash, password, provider
 * payload) or another person's personal data (a grantee's identity, the
 * other side of a conversation, a partner's clients).
 *
 * Classes that can grow without bound are paged: the first response holds
 * the first page of every class and a cursor per class with more; each
 * cursor is followed with `?class=<class>&cursor=<cursor>` until `next` is
 * null, and the items are appended to that class in order.
 */

import type {AccountSubjectReviewClass,AccountSubjectReviewDescriptor} from './account-subject-review';

export const ACCOUNT_EXPORT_FORMAT = 'tabbio.account-export' as const;
export const ACCOUNT_EXPORT_VERSION = 1 as const;

export const ACCOUNT_EXPORT_PAGED_CLASSES = [
  'cvs',
  'languageVariants',
  'shareLinks',
  'accessGrantsGiven',
  'accessGrantsReceived',
  'applications',
  'talentMemberships',
  'messagesSent',
] as const;

export type AccountExportRecordClass = `records.${string}`;
export interface AccountExportSubjectReviewReference {
  sourceClass:AccountSubjectReviewClass;
  keys:string[];
  status:'disabled'|'approved'|'review_required'|'known_other'|'missing';
  descriptor?:AccountSubjectReviewDescriptor;
}
export type AccountExportPagedClass =
  (typeof ACCOUNT_EXPORT_PAGED_CLASSES)[number] | AccountExportRecordClass;

/** What the export deliberately leaves out, by kind. */
export type AccountExportOmission =
  | 'tokens_and_secrets'
  | 'other_peoples_personal_data'
  | 'partner_client_cvs'
  | 'internal_identifiers';

export interface AccountExportSignInMethod {
  /** 'credential' | 'google' | 'apple' | ... (never a token). */
  provider: string;
  linkedAt: string;
}

export interface AccountExportAccount {
  email: string;
  emailVerified: boolean;
  name: string;
  username: string;
  image: string | null;
  status: string;
  onboardingCompleted: boolean;
  uaePassVerified: boolean;
  uaePassLinkedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  lastActivityAt: string | null;
  signInMethods: AccountExportSignInMethod[];
}

export interface AccountExportProfile {
  name: string | null;
  title: string | null;
  bio: string | null;
  about: string | null;
  avatarUrl: string | null;
  coverImageUrl: string | null;
  contactEmail: string | null;
  phone: string | null;
  location: string | null;
  socials: unknown;
  skills: string[];
  interests: string[];
  profileMode: string;
  role: string | null;
  openToWork: boolean;
  workPreferences: unknown;
  freelancePreferences: unknown;
  privateInfo: unknown;
  visibility: string;
  discoverable: boolean;
  allowPdfDownloads: boolean;
  hideFromSearchEngines: boolean;
  sectionSettings: unknown;
  status: string;
  tabbioScore: number;
  profileCompleteness: number;
  createdAt: string;
  updatedAt: string;
}

export interface AccountExportCareerEntries {
  experience: Array<Record<string, unknown>>;
  education: Array<Record<string, unknown>>;
  certifications: Array<Record<string, unknown>>;
  highlights: Array<Record<string, unknown>>;
  projects: Array<Record<string, unknown>>;
  services: Array<Record<string, unknown>>;
}

export interface AccountExportCvSubject {
  /** 'self' (your own career record) | 'partner_client' (a CV a partner built for you, which you claimed). */
  kind: string;
  /** Your Main CV pointer (self only); null means the profile-backed Main CV. */
  mainCvId: string | null;
  createdByYou: boolean;
  createdAt: string;
}

export interface AccountExportCv {
  id: string;
  title: string | null;
  summary: string | null;
  templateId: string | null;
  sections: unknown;
  defaultLocale: string | null;
  isPublic: boolean;
  slug: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface AccountExportLanguageVariant {
  /** null: an edition of the profile-backed Main CV. */
  cvId: string | null;
  targetKey: string;
  locale: string;
  direction: string;
  title: string | null;
  summary: string | null;
  headline: string | null;
  content: unknown;
  status: string;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountExportShareLink {
  cvId: string | null;
  targetKey: string;
  label: string | null;
  /** The first characters of the link token, as shown in the app; never the token. */
  tokenPreview: string | null;
  permissions: string[];
  visibility: unknown;
  allowDownload: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  accessCount: number;
  lastAccessedAt: string | null;
  createdAt: string;
}

export interface AccountExportGrantGiven {
  cvId: string | null;
  targetKey: string;
  /** Who it was given to, by kind only: their identity is their data. */
  grantee: {
    kind: 'person' | 'company' | 'invitation';
    /** A company's name (a business, not a person). */
    companyName?: string;
  };
  role: string;
  permissions: string[];
  status: string;
  acceptedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** A grant someone gave you: never their identity or their CV. */
export interface AccountExportGrantReceived {
  role: string;
  permissions: string[];
  status: string;
  acceptedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface AccountExportStatusEntry {
  status: string;
  at: string | null;
}

export interface AccountExportConsent {
  version: string | null;
  text: string | null;
  at: string | null;
}

export interface AccountExportSubmissionReceipt {
  sequence: number;
  origin: string;
  submittedAt: string;
  source: string;
  referrer: string | null;
  cv: { id: string | null; title: string | null; locale: string | null };
  /** The CV exactly as it was sent with this submission. */
  cvSnapshot: unknown;
  resumeFileName: string | null;
  consent: AccountExportConsent;
}

export interface AccountExportApplication {
  job: { title: string | null; company: string | null };
  cvId: string | null;
  status: string;
  statusHistory: AccountExportStatusEntry[];
  appliedAt: string;
  updatedAt: string;
  coverLetter: string | null;
  screeningAnswers: unknown;
  questionsSnapshot: unknown;
  jobSnapshot: unknown;
  attachments: unknown;
  cvSnapshot: unknown;
  resumeFileName: string | null;
  consent: AccountExportConsent;
  source: string;
  referrer: string | null;
  receipts: AccountExportSubmissionReceipt[];
}

export interface AccountExportTalentMembership {
  company: string | null;
  cvId: string | null;
  status: string;
  source: string;
  statusHistory: AccountExportStatusEntry[];
  submittedAt: string;
  updatedAt: string;
  withdrawnAt: string | null;
  cvSnapshot: unknown;
  resumeFileName: string | null;
  consent: AccountExportConsent;
  referrer: string | null;
  receipts: AccountExportSubmissionReceipt[];
}

/** A message you sent. Messages other people sent you are theirs. */
export interface AccountExportMessage {
  conversationId: string;
  type: string;
  content: string | null;
  attachments: unknown;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AccountExportProfileAnalytics {
  hasPage: boolean;
  /** Counts only, per event type: never a visitor, place or device. */
  events: Record<string, { total: number; qualified: number }>;
}

export interface AccountExportConnectedApps {
  apps: Array<{
    name: string;
    homepageUrl: string | null;
    grantedScopes: string[];
    /** The CV the app reads; null follows your Main CV. */
    cvId: string | null;
    sharesAvatar: boolean;
    status: string;
    createdAt: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
  }>;
  mcpClients: Array<{
    name: string | null;
    scopes: string;
    consentGiven: boolean;
    createdAt: string;
  }>;
  personalTokens: Array<{
    name: string | null;
    scopeType: string;
    scopes: string;
    createdAt: string;
    lastUsedAt: string | null;
    expiresAt: string | null;
    revokedAt: string | null;
  }>;
  connectors: Array<{
    connector: string;
    rail: string;
    status: string;
    scopes: string[];
    createdAt: string;
    lastSyncedAt: string | null;
  }>;
}

export interface AccountDataExport {
  format: typeof ACCOUNT_EXPORT_FORMAT;
  version: typeof ACCOUNT_EXPORT_VERSION;
  generatedAt: string;
  account: AccountExportAccount;
  profile: AccountExportProfile | null;
  careerEntries: AccountExportCareerEntries;
  cvSubjects: AccountExportCvSubject[];
  cvs: AccountExportCv[];
  languageVariants: AccountExportLanguageVariant[];
  shareLinks: AccountExportShareLink[];
  accessGrantsGiven: AccountExportGrantGiven[];
  accessGrantsReceived: AccountExportGrantReceived[];
  applications: AccountExportApplication[];
  talentMemberships: AccountExportTalentMembership[];
  messagesSent: AccountExportMessage[];
  publicProfileAnalytics: AccountExportProfileAnalytics;
  connectedApps: AccountExportConnectedApps;
  notificationPreferences: Record<string, unknown> | null;
  messagingPreferences: Record<string, unknown> | null;
  omitted: AccountExportOmission[];
  /** Paged own-subject classes beyond the compatibility career bundle. */
  records: Record<string, unknown[]>;
  /** Explicit model/field coverage and provider-only copies. */
  coverage: {
    exportedModels: string[];
    subjectAccessModels: string[];
    externalCopies: string[];
    excludedFields: Record<string, string[]>;
    binaryFiles: 'metadata_only_use_existing_owner_download_routes';
    /** These older nested views are convenience previews; records carries their complete paged classes. */
    compatibilityPreviews: 'career_entries_sign_in_methods_connected_apps_and_nested_submission_receipts';
    unverifiableCvSources: 'eligible_subject_review_otherwise_source_lineage_repair';
    previewLimits: {careerEntriesPerKind:number;signInMethods:number;connectedAppsPerKind:number;nestedSubmissionReceiptsPerWorkflowPage:number;cvSubjects:number};
    /** Exact selected legacy content needing review; per-row metadata also travels on every page. */
    subjectReview:{enabled:boolean;eligibleClasses:AccountSubjectReviewClass[];references:AccountExportSubjectReviewReference[]};
  };
}

/** `GET /api/account/export`. */
export interface AccountExportFirstPage {
  export: AccountDataExport;
  /** A cursor for each class with more items; absent when the class is complete. */
  next: Partial<Record<AccountExportPagedClass, string>>;
}

/** `GET /api/account/export?class=<class>&cursor=<cursor>`. */
export interface AccountExportClassPage {
  class: AccountExportPagedClass;
  items: unknown[];
  next: string | null;
}
